import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select, { components } from "react-select";
import { ChevronDown } from "lucide-react";
import ErrorMessage from "../Common/ErrorMessage";
import LocationAutocomplete from "../Common/LocationAutocomplete";
import { getCountryList } from "../../api/postgre/registerApi";
import { defaultReactSelectStyles } from "../../utils/reactSelectStyles";

const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
        <ChevronDown className="h-4 w-4 text-slate-400" />
    </components.DropdownIndicator>
);

const addressSelectStyles = {
    ...defaultReactSelectStyles,
    control: (base, state) => ({
        ...defaultReactSelectStyles.control(base),
        borderRadius: "0.75rem",
        backgroundColor: "#f7f7f7",
        borderColor: state.isFocused ? "#0f1b8f" : "#E2E8F0",
        boxShadow: "none",
        outline: "none",
        minHeight: "48px",
        "&:hover": {
            borderColor: state.isFocused ? "#0f1b8f" : "#E2E8F0",
            backgroundColor: "#f7f7f7",
        },
        "&:active": {
            backgroundColor: "#f7f7f7",
        },
    }),
};

const AddressSection = ({ control, errors, isEditing }) => {
    const { t } = useTranslation();
    const [countryOptions, setCountryOptions] = useState([]);

    useEffect(() => {
        getCountryList().then(setCountryOptions).catch(() => {});
    }, []);

    return (
        <div>
            <p className="text-sm font-semibold text-slate-900">{t("ACCOUNT_ADDRESS_TITLE")}</p>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="space-y-1 text-xs text-slate-400">
                    <span>{t("ACCOUNT_STREET")}</span>
                    <Controller
                        name="street"
                        control={control}
                        rules={{ required: t("CHECKOUT_VALIDATION_STREET_REQUIRED") }}
                        render={({ field }) => (
                            <input
                                {...field}
                                type="text"
                                disabled={!isEditing}
                                className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-700 outline-none focus:outline-none disabled:text-slate-400"
                            />
                        )}
                    />
                    {isEditing ? <ErrorMessage message={errors.street?.message} /> : null}
                </div>
                <div className="space-y-1 text-xs text-slate-400">
                    <span>{t("ACCOUNT_NUMBER")}</span>
                    <Controller
                        name="number"
                        control={control}
                        rules={{ required: t("ACCOUNT_VALIDATION_NUMBER_REQUIRED") }}
                        render={({ field }) => (
                            <input
                                {...field}
                                type="text"
                                disabled={!isEditing}
                                className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-700 outline-none focus:outline-none disabled:text-slate-400"
                            />
                        )}
                    />
                    {isEditing ? <ErrorMessage message={errors.number?.message} /> : null}
                </div>
                <div className="space-y-1 text-xs text-slate-400">
                    <span>{t("ACCOUNT_CITY")}</span>
                    <Controller
                        name="city"
                        control={control}
                        rules={{ required: t("CHECKOUT_VALIDATION_CITY_REQUIRED") }}
                        render={({ field }) => (
                            <input
                                {...field}
                                type="text"
                                disabled={!isEditing}
                                className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-700 outline-none focus:outline-none disabled:text-slate-400"
                            />
                        )}
                    />
                    {isEditing ? <ErrorMessage message={errors.city?.message} /> : null}
                </div>
                <div className="space-y-1 text-xs text-slate-400">
                    <span>{t("ACCOUNT_POSTAL_CODE")}</span>
                    <Controller
                        name="postalCode"
                        control={control}
                        rules={{ required: t("CHECKOUT_VALIDATION_POSTAL_CODE_REQUIRED") }}
                        render={({ field }) => (
                            <input
                                {...field}
                                type="text"
                                disabled={!isEditing}
                                className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-700 outline-none focus:outline-none disabled:text-slate-400"
                            />
                        )}
                    />
                    {isEditing ? <ErrorMessage message={errors.postalCode?.message} /> : null}
                </div>
                <div className="space-y-1 text-xs text-slate-400">
                    <span>{t("ACCOUNT_COUNTRY")}</span>
                    <Controller
                        name="country"
                        control={control}
                        rules={{ required: t("AUTH_VALIDATION_COUNTRY_REQUIRED") }}
                        render={({ field }) => (
                            isEditing ? (
                                <Select
                                    {...field}
                                    value={countryOptions.find((option) => option.value === field.value) || null}
                                    options={countryOptions}
                                    placeholder={t("AUTH_REGISTER_COUNTRY_PLACEHOLDER")}
                                    classNamePrefix="react-select"
                                    components={{ DropdownIndicator }}
                                    styles={addressSelectStyles}
                                    isSearchable={false}
                                    onChange={(selected) => field.onChange(selected?.value || "")}
                                />
                            ) : (
                                <div className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-400">
                                    {countryOptions.find((option) => option.value === field.value)?.label || "—"}
                                </div>
                            )
                        )}
                    />
                    {isEditing ? <ErrorMessage message={errors.country?.message} /> : null}
                </div>
                <div className="space-y-1 text-xs text-slate-400">
                    <span>{t("ACCOUNT_AREA")}</span>
                    <Controller
                        name="area"
                        control={control}
                        rules={{ required: t("CHECKOUT_VALIDATION_REGION_REQUIRED") }}
                        render={({ field }) => (
                            <LocationAutocomplete
                                value={field.value}
                                onChange={field.onChange}
                                placeholder={t("ACCOUNT_SELECT_AREA")}
                                isDisabled={!isEditing}
                            />
                        )}
                    />
                    {isEditing ? <ErrorMessage message={errors.area?.message} /> : null}
                </div>
            </div>
        </div>
    );
};

export default AddressSection;
